import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { pipLayout } from '../../utils/liveRoomUi';

/**
 * The call's floating window while its tab is in the background (decision 143), as Meet's.
 *
 * Where the browser has Document Picture-in-Picture (Chrome), the window is a small page of its own:
 * the other side's share, else the match, else the other person, and the others small under it. Chrome
 * opens it by itself when a call that is using the camera goes to the background, through the media
 * session's "enterpictureinpicture" action; no tap is needed. A click anywhere in it brings the call's
 * tab back and closes it, and so does coming back to the tab any other way.
 *
 * Elsewhere (Safari), the other person's video goes to video picture-in-picture on its own as the tab
 * goes to the background (autopictureinpicture), and the window's own button brings the tab back.
 *
 * The match is shown from its own video element (captureStream), so it plays as it does in the call.
 */

interface DocumentPip {
  window: Window | null;
  requestWindow(opts?: { width?: number; height?: number }): Promise<Window>;
}

/** Document Picture-in-Picture, where the browser has it. */
function documentPip(): DocumentPip | null {
  return (window as unknown as { documentPictureInPicture?: DocumentPip }).documentPictureInPicture ?? null;
}

/** The page's styles, into the floating window's document, so its classes mean what they mean here. */
function copyStyles(to: Document): void {
  for (const node of Array.from(document.head.querySelectorAll('style, link[rel="stylesheet"]'))) {
    to.head.appendChild(node.cloneNode(true));
  }
}

/**
 * A stream's video, cloned for the floating window (job 4, 4 Nov): the window's elements hold their own
 * tracks, so the call's own elements keep theirs and keep playing while the tab is hidden; the clones
 * stop when the window closes or the stream changes.
 */
function useClone(stream: MediaStream | null, on: boolean): MediaStream | null {
  const clone = useMemo(() => {
    if (!on || !stream) return null;
    const tracks = stream.getVideoTracks().filter((t) => t.readyState === 'live').map((t) => t.clone());
    return tracks.length ? new MediaStream(tracks) : null;
  }, [stream, on]);
  useEffect(() => () => clone?.getTracks().forEach((t) => t.stop()), [clone]);
  return clone;
}

/** The match's picture: its first panel's video, as a stream (Chrome's captureStream), or null. */
function matchStream(): MediaStream | null {
  const v = document.querySelector('[data-room-match] [data-match-panel="0"] video') as (HTMLVideoElement & { captureStream?: () => MediaStream }) | null;
  try { return v?.captureStream?.() ?? null; } catch { return null; }
}

function PipVideo({ stream, label, mirrored = false, className }: { stream: MediaStream | null; label: string; mirrored?: boolean; className: string }) {
  const ref = useCallback((el: HTMLVideoElement | null) => { if (el && el.srcObject !== stream) el.srcObject = stream; }, [stream]);
  return (
    <div className={`relative overflow-hidden bg-call-tile ${className}`} data-pip-tile={label}>
      {/* Muted: the call's own elements keep playing the sound in the tab. */}
      <video ref={ref} autoPlay playsInline muted className="h-full w-full object-contain" style={mirrored ? { transform: 'scaleX(-1)' } : undefined} />
      <span className="pointer-events-none absolute bottom-1.5 left-2 text-[12px] leading-[16px] font-medium text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.6)]">{label}</span>
    </div>
  );
}

export function CallPip({ active, theirShare, matchOpen, them, you, share, otherFirst, shareLabel, remoteVideo }: {
  /** In the call: the window may open. */
  active: boolean;
  theirShare: boolean;
  matchOpen: boolean;
  them: MediaStream | null;
  you: MediaStream | null;
  share: MediaStream | null;
  otherFirst: string;
  shareLabel: string;
  /** The stage's video, for video picture-in-picture where there is no Document Picture-in-Picture. */
  remoteVideo: () => HTMLVideoElement | null;
}) {
  const [win, setWin] = useState<Window | null>(null);
  const winRef = useRef<Window | null>(null);
  useEffect(() => { winRef.current = win; }, [win]);

  const open = useCallback(async () => {
    const dpip = documentPip();
    if (!dpip || dpip.window) return;
    try {
      const w = await dpip.requestWindow({ width: 480, height: 300 });
      copyStyles(w.document);
      w.document.body.style.margin = '0';
      w.document.body.style.background = '#000';
      w.addEventListener('pagehide', () => { winRef.current = null; setWin(null); }, { once: true });
      winRef.current = w;
      setWin(w);
    } catch { /* refused: the call goes on in the tab */ }
  }, []);

  // Closing fires the window's pagehide, which clears it.
  const close = useCallback(() => { winRef.current?.close(); }, []);

  // Chrome: the media session's action, which the browser itself calls as the call's tab goes to the background.
  useEffect(() => {
    if (!active || !documentPip() || !('mediaSession' in navigator)) return;
    const session = navigator.mediaSession as MediaSession & { setActionHandler(a: string, h: (() => void) | null): void };
    try { session.setActionHandler('enterpictureinpicture', () => { void open(); }); } catch { return; }
    return () => { try { session.setActionHandler('enterpictureinpicture', null); } catch { /* gone */ } };
  }, [active, open]);

  // Back on the tab, the window goes; leaving the call closes it.
  useEffect(() => {
    if (!win) return;
    const onVisible = () => { if (document.visibilityState === 'visible') { close(); setWin(null); } };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [win, close]);
  useEffect(() => { if (!active) close(); }, [active, close]);
  useEffect(() => close, [close]);

  // Safari and others: video picture-in-picture of the stage, on its own as the tab goes.
  useEffect(() => {
    if (!active || documentPip()) return;
    const v = remoteVideo() as (HTMLVideoElement & { autoPictureInPicture?: boolean }) | null;
    if (!v) return;
    v.setAttribute('autopictureinpicture', '');
    v.autoPictureInPicture = true;
    return () => { v.removeAttribute('autopictureinpicture'); v.autoPictureInPicture = false; };
  }, [active, remoteVideo]);

  const layout = pipLayout({ theirShare, matchOpen });
  // The match's picture, taken from its video while the window is open on it, let go when that ends.
  const match = useMemo(() => (win && layout.main === 'match' ? matchStream() : null), [win, layout.main]);
  useEffect(() => () => match?.getTracks().forEach((t) => t.stop()), [match]);

  const isOpen = !!win;
  const themClone = useClone(them, isOpen);
  const youClone = useClone(you, isOpen);
  const shareClone = useClone(share, isOpen && layout.main === 'share');
  if (!win) return null;
  const main = layout.main === 'share' ? shareClone : layout.main === 'match' ? (match ?? themClone) : themClone;
  const mainLabel = layout.main === 'share' ? shareLabel : layout.main === 'match' ? (match ? 'Match' : otherFirst) : otherFirst;
  return createPortal(
    <button type="button" aria-label="Back to the call" title="Back to the call" data-call-pip={layout.main}
      onClick={() => { window.focus(); close(); setWin(null); }}
      className="relative block h-screen w-screen cursor-pointer overflow-hidden bg-call-bezel p-1.5 text-left">
      <PipVideo stream={main} label={mainLabel} className="h-full w-full rounded-[8px]" />
      <div className="absolute bottom-3 right-3 flex gap-1.5">
        {layout.tiles.map((t) => (
          <PipVideo key={t} stream={t === 'them' ? themClone : youClone} label={t === 'them' ? otherFirst : 'You'} mirrored={t === 'you'}
            className="aspect-video w-28 rounded-[8px] shadow-[0_2px_12px_rgba(0,0,0,0.4)]" />
        ))}
      </div>
    </button>,
    win.document.body,
  );
}
